import { TaskOutput } from "@coso/core/workflow/schema";
import { MediaSchema, PostSchema, TaskStatusSchema } from "../prisma/zod";
import { WebPageSchema } from "../webcrawler/schema";
import { z } from "zod";

export const RTStatus = z.enum([
  "connected",
  "connecting",
  "disconnected",
  "error",
  "stopped",
]);
export type RTStatus = z.infer<typeof RTStatus>;

const topics = z.enum([
  "image.photoshoot",
  "user.notification",
  "task.status",
  "task.debug",
  "workflow.status",
]);

const userNotification = z.object({
  title: z.string(),
  message: z.string(),
  images: z.string().optional(),
  type: z.enum(["popup", "toast"]),
  level: z.enum(["success", "info", "warning", "error"]),
  action: z.enum(["reload", "invalidate"]).optional(),
  url: z.string().optional(),
  duration: z.number().optional(),
});

const taskStatusUpdate = z.object({
  status: TaskStatusSchema,
  id: z.string(),
});
const taskDebugUpdate = z.object({
  data: z.any().optional(),
  type: z.enum(["string", "json", "image", "url"]),
  taskName: z.string(),
  id: z.string(),
});

const workflowStatusUpdate = z.object({
  status: z.enum(["running", "debugging", "idle", "error"]),
  result: z.array(TaskOutput).optional(),
  id: z.string(),
});

export type UserNotification = z.infer<typeof userNotification>;

export const realtimeEvent = z.discriminatedUnion("topic", [
  z.object({
    topic: z.literal(topics.enum["user.notification"]),
    payload: userNotification,
  }),
  z.object({
    topic: z.literal("onboarding.data"),
    payload: z.array(z.any()),
  }),
  z.object({
    topic: z.literal("image.photoshoot"),
    payload: z.record(z.string(), z.array(z.string())),
  }),
  z.object({
    topic: z.literal("image.edit"),
    payload: z.record(z.string(), z.any()),
  }),
  z.object({
    topic: z.literal(topics.enum["task.status"]),
    payload: taskStatusUpdate,
  }),
  z.object({
    topic: z.literal(topics.enum["task.debug"]),
    payload: taskDebugUpdate,
  }),
  z.object({
    topic: z.literal(topics.enum["workflow.status"]),
    payload: workflowStatusUpdate,
  }),
  z.object({ topic: z.literal("webcrawler.webpage"), payload: WebPageSchema }),
  z.object({
    topic: z.literal("post.created"),
    payload: PostSchema.extend({
      attachments: z.array(
        z.object({
          id: z.string(),
          mediaKey: z.string(),
          postId: z.string(),
          edits: z.any(),
          rendered: z.string().nullable().optional(),
          media: MediaSchema,
        }).or(z.object({ loading: z.boolean() }))
      ),
    }),
  }),
  z.object({
    topic: z.literal("post.status"),
    payload: z.object({
      postId: z.string(),
      key: z.enum([
        "caption_loading",
        "attachments_loading",
      ]),
      value: z.union([z.boolean(), z.number()]),
    }),
  }),
  z.object({
    topic: z.literal("text.created"),
    payload: z.object({
      content: z.string(),
      role: z.enum(["assistant", "user", "system"]),
    }),
  }),
  z.object({
    topic: z.literal("media.created"),
    payload: MediaSchema,
  }),
]);

export type RealtimeEvent = z.infer<typeof realtimeEvent>;
